import Settings from "./settings";
import { notification, message } from "antd";
import { resetAuthStore } from "../../store/values-store";

const Utilities = {
  //CUSTOM UTILITY FUNCTIONS
  formatAccounting(value) {
    if (value == null || isNaN(value)) return "";

    // Ensure it's a number
    const num = Number(value);

    // Format with commas and 2 decimals
    const formatted = Math.abs(num).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    // Wrap negatives in parentheses
    return num < 0 ? `(${formatted})` : formatted;
  },

  hasPermissionV2(
    methods = [],
    permList = [],
    perm = "",
    baseUrl = `${Settings.backend}`,
    permKey = "api_path",
    methodKey = "method",
  ) {
    if (!Array.isArray(permList)) return false;
    // helper to normalize paths (remove trailing slashes, lowercase)
    const normalize = (str) => str?.replace(/\/+$/, "").toLowerCase();

    return permList?.some((p) => {
      const rawPath = p?.[permKey];
      const method = p?.[methodKey];

      if (!rawPath || !method) return false;

      const fullPath = `${baseUrl}${rawPath}`;
      const normalizedPerm = normalize(perm);
      const normalizedPath = normalize(fullPath);
      // console.log(normalizedPerm, normalizedPath);
      // check if rawPath looks like a regex pattern
      let pathMatches = false;
      if (/^(\^.*\$)$/.test(rawPath)) {
        try {
          const regex = new RegExp(rawPath);
          pathMatches = regex.test(perm.replace(baseUrl, "")); // test against relative path
        } catch (e) {
          console.warn("Invalid regex in permission:", rawPath);
        }
      } else {
        // console.log(normalizedPath,normalizedPerm,normalizedPath === normalizedPerm,method,methods);
        pathMatches = normalizedPath === normalizedPerm;
      }

      return (
        pathMatches &&
        (methods.some((m) => m?.toLowerCase() === method?.toLowerCase()) ||
          method === "*")
      );
    });
  },

  isPlainObject(item) {
    return (
      typeof item === "object" && // Checks if it's a non-primitive type, including null, arrays, dates
      item !== null && // Excludes null (which typeof reports as 'object')
      !Array.isArray(item) // Excludes arrays
      // Add additional checks to exclude Date, RegExp, etc. if needed
    );
  },
  cleanupObject(data) {
    //for removing certain falsy values from an object
    return Object.fromEntries(
      Object.entries(data).filter(([_, value]) => {
        if (value === null || value === undefined || Number.isNaN(value)) {
          return false;
        }
        if (
          Array.isArray(value) &&
          value.every((v) => v === null || v === undefined || Number.isNaN(v))
        ) {
          return false;
        }
        return true;
      }),
    );
  },
  removeDuplicateUsingKey(entries = [], key) {
    const list = [];
    const final = [];
    for (let entry of entries) {
      if (!list.includes(entry?.[key])) {
        final.push(entry);
        list?.push(entry?.[key]);
      }
    }
    return final;
  },
  isPdf: (url) => url?.toLowerCase().endsWith(".pdf"),
  capitalizeFirstLetter(str) {
    if (typeof str !== "string" || str.length === 0) {
      return str; // Handle empty or non-string inputs
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
  },
  openPopup(url) {
    const width = 800;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    window.open(
      url,
      "popupWindow",
      `width=${width},height=${height},top=${top},left=${left},scrollbars=yes,resizable=yes`,
    );
  },
  isNumeric: (str) => Number.isFinite(+str),
  async fetchWithAuth(
    method = "GET",
    url,
    data = {},
    navigate,
    fallbackUrl = "../../signin",
    options = {},
  ) {
    // normalize data to an object
    if (data == null) data = {};
    const accessToken = Utilities.getCookie("access");
    const refreshToken = Utilities.getCookie("refresh");

    let headers = {
      ...options.headers,
    };

    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }

    let body = null;

    //Detect if we’re sending files
    const isFormData = data instanceof FormData;

    // const hasFile =
    //     isFormData ||
    //     Object.values(data).some(
    //         (v) =>
    //             v instanceof File ||
    //             v instanceof Blob ||
    //             (Array.isArray(v) && v.some((f) => f instanceof File || f instanceof Blob))
    //     );

    const hasFile =
      isFormData ||
      (data &&
        Object.values(data).some(
          (v) =>
            v instanceof File ||
            v instanceof Blob ||
            (Array.isArray(v) &&
              v.some((f) => f instanceof File || f instanceof Blob)),
        ));

    if (method !== "GET" && data) {
      if (hasFile) {
        let formData;

        if (isFormData) {
          formData = data;
        } else {
          formData = new FormData();
          for (const key in data) {
            const value = data[key];
            if (Array.isArray(value)) {
              value.forEach((item) => {
                formData.append(`${key}[]`, item);
              });
            } else {
              formData.append(key, value);
            }
          }
        }

        body = formData;
        delete headers["Content-Type"]; // let browser set the boundary
      } else {
        headers["Content-Type"] = "application/json";
        body = JSON.stringify(data);
      }
    }

    const fetchOptions = {
      method,
      headers,
      body: method !== "GET" ? body : null,
    };
    const isAuthRequest = [
      Settings.urls.auth.login,
      Settings.urls.auth.refresh_token,
    ].includes(url);

    const parseResponseBody = async (response, options = {}) => {
      if (options.responseType === "blob") {
        return await response.blob();
      }

      const contentType = response?.headers?.get("content-type") || "";

      try {
        if (contentType.includes("application/json")) {
          return await response.json();
        }

        return await response.text();
      } catch (error) {
        return null;
      }
    };

    try {
      const response = await fetch(url, fetchOptions);

      // Handle expired token with retry
      if (
        response?.status === 401 &&
        refreshToken &&
        accessToken &&
        !isAuthRequest
      ) {
        const refreshData = await Utilities.refreshTokenWithRetry();

        if (!refreshData) {
          Utilities.triggerSessionExpiry();
          return {
            data: null,
            status: 401,
            msg: "Session expired. Please sign in again.",
            ok: false,
          };
        }

        const newAccess = refreshData.access;

        // Retry original request with new token
        const retryHeaders = {
          ...headers,
          Authorization: `Bearer ${newAccess}`,
        };
        const retryResponse = await fetch(url, {
          ...fetchOptions,
          headers: retryHeaders,
        });

        if (!retryResponse?.ok) {
          const retryError = await parseResponseBody(retryResponse);
          return {
            data: typeof retryError === "object" ? retryError : null,
            status: retryResponse?.status,
            msg:
              typeof retryError === "string"
                ? retryError
                : retryResponse?.statusText,
            ok: false,
          };
        }

        const retryData = await parseResponseBody(retryResponse, options);
        return {
          data: retryData,
          status: retryResponse?.status,
          msg: retryResponse?.statusText,
          ok: retryResponse?.ok,
        };
      }

      //  Handle non-OK response
      if (!response?.ok) {
        const errorPayload = await parseResponseBody(response);
        return {
          data: typeof errorPayload === "object" ? errorPayload : null,
          status: response?.status,
          msg:
            typeof errorPayload === "string"
              ? errorPayload
              : response?.statusText,
          ok: false,
        };
      }

      // Successful response
      const resData = await parseResponseBody(response, options);

      // message.success("Request successful");
      return {
        data: resData,
        status: response?.status,
        msg: response?.statusText,
        ok: true,
      };
    } catch (error) {
      // console.error('API error:', error);
      // message.error("Network error. Please try again later.");
      return { data: null, status: 500, msg: error?.message, ok: false };
    }
  },
  // Session expiry callback — set by App.js to trigger the expired modal
  _onSessionExpired: null,
  onSessionExpired(callback) {
    Utilities._onSessionExpired = callback;
  },
  triggerSessionExpiry() {
    if (Utilities._onSessionExpired) {
      Utilities._onSessionExpired();
    }
  },

  async refreshTokenWithRetry(maxRetries = 3) {
    const refreshToken = Utilities.getCookie("refresh");
    if (!refreshToken) return null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const res = await fetch(Settings.urls.auth.refresh_token, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh: refreshToken }),
        });
        if (res.ok) {
          const data = await res.json();
          Utilities.setCookie("access", data.access);
          if (data.refresh) Utilities.setCookie("refresh", data.refresh);
          return data;
        }
        // 4xx means token is truly invalid — don't retry
        if (res.status >= 400 && res.status < 500) return null;
      } catch (err) {
        // Network error — will retry
      }
      if (attempt < maxRetries - 1) {
        await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt)));
      }
    }
    return null;
  },

  _refreshTimerId: null,
  startTokenRefreshTimer(ms = 180000) {
    if (Utilities._refreshTimerId) return Utilities._refreshTimerId;

    const refreshToken = Utilities.getCookie("refresh");
    if (!refreshToken) return null;

    Utilities._refreshTimerId = setInterval(async () => {
      const result = await Utilities.refreshTokenWithRetry();
      if (!result) {
        Utilities.triggerSessionExpiry();
      }
    }, ms);

    return Utilities._refreshTimerId;
  },
  stopTokenRefreshTimer() {
    if (Utilities._refreshTimerId) {
      clearInterval(Utilities._refreshTimerId);
      Utilities._refreshTimerId = null;
    }
  },
  createArrayFromSingleObjectKey(item, key) {
    return item?.map((v) => {
      return v[key];
    });
  },

  getDuplicateObjectsByKey(arrayObjects, key) {
    const seen = new Map();
    const duplicates = {};
    for (const obj of arrayObjects) {
      const value = obj?.[key];
      if (seen.has(value)) {
        if (!duplicates[value]) {
          duplicates[value] = [seen.get(value)];
        }
        duplicates[value].push(obj);
      } else {
        seen.set(value, obj);
      }
    }
    return duplicates;
  },

  async getMimeTypeFromUrl(url) {
    try {
      const response = await fetch(url, { method: "HEAD" });
      const contentType = response.headers.get("Content-Type");
      return contentType || "unknown";
    } catch (error) {
      console.error("Failed to fetch MIME type:", error);
      return "error";
    }
  },
  openPdf(url, width = 800, height = 800) {
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;

    window.open(
      url,
      "_blank",
      `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`,
    );
  },

  objectFromArray(arrayOfObjects = [], key, value) {
    if (!Array.isArray(arrayOfObjects)) return {};
    return arrayOfObjects?.reduce((acc, currentItem) => {
      acc[currentItem?.[key]] = currentItem?.[value];
      return acc;
    }, {});
  },
  // createObjectFromArrayObjects(arrayObjects = [], keyProp, valueProp) {
  //     let res = {};
  //     for (let entry of arrayObjects) {
  //         res[entry[keyProp]] = entry[valueProp];
  //     }
  //     return res;
  // },
  async logout(
    navigate,
    accessTokenKey = "token",
    refreshTokenKey = "token",
    redirectUrl = "../signin",
  ) {
    const refreshToken = Utilities.getCookie("refresh");
    const res = await Utilities.fetchWithAuth(
      "POST",
      Settings.urls.auth.logout,
      { refresh: refreshToken },
      navigate,
    );
    Utilities.setCookie(accessTokenKey, "", 0);
    Utilities.setCookie(refreshTokenKey, "", 0);
    Utilities.clearSessionLock();
    resetAuthStore();
    if (navigate) {
      sessionStorage.clear();
      navigate?.(redirectUrl);
    }
  },
  setPropFromStore(
    valuesStore,
    setDateFormat,
    storeStateKey = "settings",
    where = "property",
    value = "Date_Format",
  ) {
    const settings = valuesStore.getValuesBy(storeStateKey, where, value);
    if (settings?.length) {
      setDateFormat(settings[0]?.value);
    }
  },
  scrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: "smooth", // for smooth scrolling
    });
  },
  scrollToElem(elemRef) {
    elemRef?.current?.scrollIntoView({ behavior: "smooth" });
  },

  setCookie(cname, cvalue, expDays) {
    if (expDays === 0 || !cvalue) {
      sessionStorage.removeItem(cname);
    } else {
      sessionStorage.setItem(cname, cvalue);
      if (
        (cname === "access" || cname === "refresh") &&
        Utilities.hasActiveSession()
      ) {
        const lock = Utilities.getSessionLock();
        if (lock) {
          lock[cname] = cvalue;
          lock.timestamp = Date.now();
          localStorage.setItem("edunex_session_lock", JSON.stringify(lock));
        }
      }
    }
  },

  getCookie(cname) {
    return sessionStorage.getItem(cname) || "";
  },

  setSessionLock(userId, userData = {}) {
    localStorage.setItem(
      "edunex_session_lock",
      JSON.stringify({
        userId,
        access: sessionStorage.getItem("access") || "",
        refresh: sessionStorage.getItem("refresh") || "",
        user:
          userData.user || JSON.parse(sessionStorage.getItem("user") || "null"),
        admissions:
          userData.admissions ||
          JSON.parse(sessionStorage.getItem("admissions") || "null"),
        timestamp: Date.now(),
      }),
    );
  },

  getSessionLock() {
    try {
      const lock = localStorage.getItem("edunex_session_lock");
      return lock ? JSON.parse(lock) : null;
    } catch {
      return null;
    }
  },

  clearSessionLock() {
    localStorage.removeItem("edunex_session_lock");
  },

  hasActiveSession() {
    const lock = Utilities.getSessionLock();
    if (!lock) return false;
    return !!lock.userId && !!lock.access;
  },

  // Restores session into this tab's sessionStorage from the cross-tab lock
  restoreSessionToTab() {
    const lock = Utilities.getSessionLock();
    if (!lock?.access) return false;

    sessionStorage.setItem("access", lock.access);
    sessionStorage.setItem("refresh", lock.refresh);
    if (lock.user) sessionStorage.setItem("user", JSON.stringify(lock.user));
    if (lock.admissions)
      sessionStorage.setItem("admissions", JSON.stringify(lock.admissions));
    return true;
  },

  groupBy: function (xs, key) {
    return xs?.reduce(function (rv, x) {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {});
  },

  showNotification(
    msg = "Attention",
    description,
    type = "text-danger",
    placement = "bottomRight",
  ) {
    if (!description) return;
    notification.open({
      message: (
        <label className={`fw-bolder ${type}`}>
          <i className="fas fa-exclamation-circle"></i> {msg}
        </label>
      ),
      description: description,
      placement: placement,
    });
  },

  ValidationFactory: {
    //STRING VALIDATION  +(match one or more) *(match zero or more)
    validateEmptyString: (val) => {
      if (val) {
        return true;
      } else {
        return false;
      }
    },
    validateAlpha: (val) => {
      let pattern = /^[a-zA-Z]+$/;
      if (pattern.test(val?.trim())) {
        return true;
      } else {
        return false;
      }
    },
    validateAlphaAllowSpace: (val) => {
      let pattern = /^[a-zA-Z ]+$/;
      if (pattern.test(val?.trim())) {
        return true;
      } else {
        return false;
      }
    },
    validateAlphaMinCharLength: (val, requiredMinCharLength) => {
      let valid = Utilities.ValidationFactory.validateAlpha(val);
      if (val && valid && val?.trim().length >= requiredMinCharLength) {
        return true;
      } else {
        return false;
      }
    },
    validateAlphaMaxCharLength: (val, requiredMaxCharLength) => {
      let valid = Utilities.ValidationFactory.validateAlpha(val);
      if (val && valid && val?.trim().length <= requiredMaxCharLength) {
        return true;
      } else {
        return false;
      }
    },
    validateAlphaMinMaxCharLength: (
      val,
      requiredMinCharLength,
      requiredMaxCharLength,
    ) => {
      let valid = Utilities.ValidationFactory.validateAlpha(val);
      if (
        val &&
        valid &&
        val?.trim().length >= requiredMinCharLength &&
        val?.trim().length <= requiredMaxCharLength
      ) {
        return true;
      } else {
        return false;
      }
    },
    validateAlphaMinCharLengthAllowSpace: (val, requiredMinCharLength) => {
      let valid = Utilities.ValidationFactory.validateAlphaAllowSpace(val);
      if (val && valid && val?.trim().length >= requiredMinCharLength) {
        return true;
      } else {
        return false;
      }
    },
    validateAlphaMaxCharLengthAllowSpace: (val, requiredMaxCharLength) => {
      let valid = Utilities.ValidationFactory.validateAlphaAllowSpace(val);
      if (val && valid && val?.trim().length <= requiredMaxCharLength) {
        return true;
      } else {
        return false;
      }
    },
    validateAlphaMinMaxCharLengthAllowSpace: (
      val,
      requiredMinCharLength,
      requiredMaxCharLength,
    ) => {
      let valid = Utilities.ValidationFactory.validateAlphaAllowSpace(val);
      if (
        val &&
        valid &&
        val?.trim().length >= requiredMinCharLength &&
        val?.trim().length <= requiredMaxCharLength
      ) {
        return true;
      } else {
        return false;
      }
    },

    //NUMBER VALIDATION
    validateNumber: function (val) {
      let pattern = /^[\d]+$/;
      if (pattern.test(val?.trim())) {
        return true;
      } else {
        return false;
      }
    },
    validateDecNumber: function (val) {
      let pattern = /^[\d.]+$/;
      if (pattern.test(val?.trim())) {
        return true;
      } else {
        return false;
      }
    },
    validateNumberAllowSpace: function (val) {
      let pattern = /^[\d ]+$/;
      if (pattern.test(val?.trim())) {
        return true;
      } else {
        return false;
      }
    },
    validateNumberMinCharLength: function (val, requiredMinCharLength) {
      let valid = Utilities.ValidationFactory.validateNumber(val);
      if (val && valid && val?.trim().length >= requiredMinCharLength) {
        return true;
      } else {
        return false;
      }
    },
    validateNumberMaxCharLength: function (val, requiredMaxCharLength) {
      let valid = Utilities.ValidationFactory.validateNumber(val);
      if (val && valid && val?.trim().length <= requiredMaxCharLength) {
        return true;
      } else {
        return false;
      }
    },
    validateNumberMinMaxCharLength: function (
      val,
      requiredMinCharLength,
      requiredMaxCharLength,
    ) {
      let valid = Utilities.ValidationFactory.validateNumber(val);
      if (
        val &&
        valid &&
        val?.trim().length >= requiredMinCharLength &&
        val?.trim().length <= requiredMaxCharLength
      ) {
        return true;
      } else {
        return false;
      }
    },

    validateNumberMinCharLengthAllowSpace: function (
      val,
      requiredMinCharLength,
    ) {
      let valid = Utilities.ValidationFactory.validateNumberAllowSpace(val);
      if (val && valid && val?.trim().length >= requiredMinCharLength) {
        return true;
      } else {
        return false;
      }
    },
    validateNumberMaxCharLengthAllowSpace: function (
      val,
      requiredMaxCharLength,
    ) {
      let valid = Utilities.ValidationFactory.validateNumberAllowSpace(val);
      if (val && valid && val?.trim().length <= requiredMaxCharLength) {
        return true;
      } else {
        return false;
      }
    },
    validateNumberMinMaxCharLengthAllowSpace: function (
      val,
      requiredMinCharLength,
      requiredMaxCharLength,
    ) {
      let valid = Utilities.ValidationFactory.validateNumberAllowSpace(val);
      if (
        val &&
        valid &&
        val?.trim().length >= requiredMinCharLength &&
        val?.trim().length <= requiredMaxCharLength
      ) {
        return true;
      } else {
        return false;
      }
    },

    //ALPHANUMERIC VALIDATION
    validateAlphaNumeric: (val) => {
      let pattern = /^[0-9a-zA-Z]+$/;
      if (pattern.test(val?.trim())) {
        return true;
      } else {
        return false;
      }
    },
    validateAlphaNumericAllowSpace: (val) => {
      let pattern = /^[0-9a-zA-Z ]+$/;
      if (pattern.test(val?.trim())) {
        return true;
      } else {
        return false;
      }
    },
    validateAlphaNumericAllowExtraChar: (val) => {
      let pattern = /^[0-9a-zA-Z.@$#_ ]+$/;
      if (pattern.test(val?.trim())) {
        return true;
      } else {
        return false;
      }
    },
    validateAlphaNumericMinCharLength: (val, requiredMinCharLength) => {
      let valid = Utilities.ValidationFactory.validateAlphaNumeric(val);
      if (val && valid && val?.trim().length >= requiredMinCharLength) {
        return true;
      } else {
        return false;
      }
    },
    validateAlphaNumericMaxCharLength: (val, requiredMaxCharLength) => {
      let valid = Utilities.ValidationFactory.validateAlphaNumeric(val);
      if (val && valid && val?.trim().length <= requiredMaxCharLength) {
        return true;
      } else {
        return false;
      }
    },
    validateAlphaNumericMinMaxCharLength: (
      val,
      requiredMinCharLength,
      requiredMaxCharLength,
    ) => {
      let valid = Utilities.ValidationFactory.validateAlphaNumeric(val);
      if (
        val &&
        valid &&
        val?.trim().length >= requiredMinCharLength &&
        val?.trim().length <= requiredMaxCharLength
      ) {
        return true;
      } else {
        return false;
      }
    },
    validateAlphaNumericMinCharLengthAllowSpace: (
      val,
      requiredMinCharLength,
    ) => {
      let valid =
        Utilities.ValidationFactory.validateAlphaNumericAllowSpace(val);
      if (val && valid && val?.trim().length >= requiredMinCharLength) {
        return true;
      } else {
        return false;
      }
    },
    validateAlphaNumericMaxCharLengthAllowSpace: (
      val,
      requiredMaxCharLength,
    ) => {
      let valid =
        Utilities.ValidationFactory.validateAlphaNumericAllowSpace(val);
      if (val && valid && val?.trim().length <= requiredMaxCharLength) {
        return true;
      } else {
        return false;
      }
    },
    validateAlphaNumericMinMaxCharLengthAllowSpace: (
      val,
      requiredMinCharLength,
      requiredMaxCharLength,
    ) => {
      let valid =
        Utilities.ValidationFactory.validateAlphaNumericAllowSpace(val);
      if (
        val &&
        valid &&
        val?.trim()?.length >= requiredMinCharLength &&
        val?.trim()?.length <= requiredMaxCharLength
      ) {
        return true;
      } else {
        return false;
      }
    },

    //EMAIL VALIDATION
    validateEmailWithUnicode: function (val) {
      let pattern =
        /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
      if (pattern.test(val?.trim())) {
        return true;
      } else {
        return false;
      }
    },
    validateEmailNoUnicode: function (val) {
      let pattern =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (pattern.test(val?.trim())) {
        return true;
      } else {
        return false;
      }
    },
    validateSimpleEmail: function (val) {
      let pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (pattern.test(val?.trim())) {
        return true;
      } else {
        return false;
      }
    },

    //TELEPHONE NUMBER VALIDATION
    validateTelephoneNumber: function (val) {
      let pattern = /^$/;
      if (pattern.test(val?.trim())) {
        return true;
      } else {
        return false;
      }
    },

    //LOOSE VALIDATION
    looselyValidateNumber: (val) => {
      if (!val) {
        //if the val is empty, don't check anything
        return true;
      } else {
        //if the val isn't empty, check if the value conforms to this pattern
        return Utilities.ValidationFactory.validateNumber(val);
      }
    },
    looselyValidateDecNumber: (val) => {
      if (!val) {
        //if the val is empty, don't check anything
        return true;
      } else {
        //if the val isn't empty, check if the value conforms to this pattern
        return Utilities.ValidationFactory.validateDecNumber(val);
      }
    },
    looselyValidateAlphaNumericAllowSpace: (val) => {
      if (!val) {
        //if the val is empty, don't check anything
        return true;
      } else {
        //if the val isn't empty, check if the value conforms to this pattern
        return Utilities.ValidationFactory.validateAlphaNumericAllowSpace(val);
      }
    },
    looselyValidateEmailNoUnicode: (val) => {
      if (!val) {
        //if the val is empty, don't check anything
        return true;
      } else {
        //if the val isn't empty, check if the value conforms to this pattern
        return Utilities.ValidationFactory.validateEmailNoUnicode(val);
      }
    },
    looselyValidateEmailWithUnicode: (val) => {
      if (!val) {
        //if the val is empty, don't check anything
        return true;
      } else {
        //if the val isn't empty, check if the value conforms to this pattern
        return Utilities.ValidationFactory.validateEmailWithUnicode(val);
      }
    },
    looselyValidatePlainString: (val) => {
      if (!val) {
        //if the val is empty, don't check anything
        return true;
      } else {
        //if the val isn't empty, check if the value conforms to this pattern
        return Utilities.ValidationFactory.validateAlpha(val);
      }
    },
    looselyValidateAlphaNumericAllowExtraChar: (val) => {
      if (!val) {
        //if the val is empty, don't check anything
        return true;
      } else {
        //if the val isn't empty, check if the value conforms to this pattern
        return Utilities.ValidationFactory.validateAlphaNumericAllowExtraChar(
          val,
        );
      }
    },
    noValidation: () => {
      return true;
    },
    validateYYYYMMDate: (val) => {
      let pattern = /^\d{4}\-(0[1-9]|1[012])$/gm;
      if (pattern.test(val?.trim())) {
        return true;
      } else {
        return false;
      }
    },
    //IS SAME
  },

  // validateSoftData(data) {
  //     const val = data.value;
  //     const validator = data.validator?.split('(');
  //     const args = (validator && validator?.length) ? validator[1]?.replace(')', '')?.split(',') : [];
  //     const required = data.required;
  //     const funcName = (validator && validator?.length) ? validator[0] : '';
  //     if (validator && required) {
  //         const valid = args ? Utilities.ValidationFactory[funcName](val, ...args) : Utilities.ValidationFactory[funcName](val);
  //         return { valid, ...data };
  //     }
  //     return { valid: true, ...data };
  // },

  validateSoftData(data) {
    const val = data.value; // Extract the value to be validated from the data object
    const validator = data.validator.split("("); // Split the validator string to get the validator name and arguments
    const args = validator[1]?.replace(")", "").split(",") || []; // Extract the arguments from the validator string
    const required = data.required; // Check if the validation is required
    if (required) {
      // If validation is required
      const validationFunction = Utilities.ValidationFactory[validator[0]]; // Extract the validation function name
      if (!validationFunction) {
        throw new Error(
          `Validator "${validator[0]}" not found in ValidationFactory.`,
        );
      }
      const valid = validationFunction(val, ...args); // Call the specified validator function with the value and arguments
      return { valid, ...data }; // Return an object with the validation result and other data
    }
    return { valid: true, ...data }; // If validation is not required, return true
  },

  validateSoftListData(data) {
    const valids = [];
    for (let i = 0; i < data.length; i++) {
      const val = data[i].value;
      const name = data[i].name;
      const validator = data[i].validator.split("(");
      const args = validator[1]?.replace(")", "").split(",") || [];
      const extra = { ...data[i] };
      const required = data[i].required;
      if (required) {
        const valid = Utilities.ValidationFactory[validator](val, ...args);
        if (!valid) {
          return { valid, name };
        } else {
          valids.push({ ...data[i] });
        }
      } else {
        valids.push({ ...data[i] });
      }
    }
    return { data: valids, valid: true };
  },
  //for disabling auto zoom in on iphone/ipad
  addMaximumScaleToMetaViewport: () => {
    const el = document.querySelector("meta[name=viewport]");
    if (el !== null) {
      let content = el.getAttribute("content");
      let re = /maximum\-scale=[0-9\.]+/g;
      if (re.test(content)) {
        content = content.replace(re, "maximum-scale=1.0");
      } else {
        content = [content, "maximum-scale=1.0"].join(", ");
      }
      el.setAttribute("content", content);
    }
  },

  checkIsIOS: () =>
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream,

  getRandomColor() {
    var letters = "0123456789ABCDEF";
    var color = "#";
    for (var i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  },
  getRandomcolorV2(red = 256, green = 256, blue = 256) {
    red = red > 256 ? 256 : red;
    green = green > 256 ? 256 : green;
    blue = blue > 256 ? 256 : blue;
    function c(range) {
      var hex = Math.floor(Math.random() * range).toString(16);
      return ("0" + String(hex)).substr(-2); // pad with zero
    }
    return "#" + c(red) + c(green) + c(blue);
  },
  convertObjectValsToString(data) {
    const a = {};
    for (let key in data) {
      a[key] = String(data[key]);
    }
    return a;
  },
  formatDate(date, joiner = "-") {
    var d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join(joiner);
  },

  isDate(dateStr) {
    return !isNaN(new Date(dateStr).getDate());
  },

  getQString() {
    let sp = {};
    const search = window.location.search.replace("?", "").split("&");
    search.forEach((s) => {
      const params = new Proxy(new URLSearchParams(window.location.search), {
        get: (searchParams, prop) => searchParams.get(prop),
      });
      const sh = s.split("=");
      if (sh[0] && sh[1]) {
        sp[sh[0]] = params[sh[0]];
      }
    });
    return sp;
  },

  getHString() {
    let sp = {};
    const hash = window.location.hash.split("&");
    hash.forEach((s) => {
      const params = new Proxy(new URLSearchParams(window.location.hash), {
        get: (searchParams, prop) => searchParams.get(prop),
      });
      const sh = s.split("=");
      if (sh[0] && sh[1]) {
        sp[sh[0].replace("#", "")] = params[sh[0]];
      }
    });
    return sp;
  },

  getUrlFileName() {
    let url = window.location.pathname;
    let filename = url.substring(url.lastIndexOf("/") + 1);
    return filename;
  },

  sleep: (ms) => new Promise((r) => setTimeout(r, ms)),

  async getSettings(valuesStore, key, ms = 500) {
    await Utilities.sleep(ms);
    return valuesStore.getValuesBy("settings", "prop", key)[0]?.["value"];
  },

  generateUuid() {
    let s = [];
    let hexDigits = "0123456789abcdef";
    for (let i = 0; i < 36; i++) {
      s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
    }
    s[14] = "4"; // bits 12-15 of the time_hi_and_version field to 0010
    s[19] = hexDigits.substr((s[19] & 0x3) | 0x8, 1); // bits 6-7 of the clock_seq_hi_and_reserved to 01
    s[8] = s[13] = s[18] = s[23];

    let uuid = s.join("");
    return uuid;
  }, //end of function

  /**
   * Format API error response into a user-friendly message
   * Handles various error formats: {error, details}, {message}, {detail}, string, etc.
   * @param {Object} response - The API response object from fetchWithAuth
   * @param {string} fallbackMessage - Default message if no error can be extracted
   * @returns {string} User-friendly error message
   */
  formatErrorMessage(response, fallbackMessage = "An error occurred") {
    console.log("recurring!!!");
    if (!response) return fallbackMessage;

    // If msg is already a clean string message
    if (
      response.msg &&
      typeof response.msg === "string" &&
      !response.msg.startsWith("{")
    ) {
      return response.msg;
    }

    // If we have parsed error data
    if (response.data) {
      const data = response.data;

      // Handle {error: "message", details: {...}} format
      if (data.error) {
        let errorMsg = data.error;

        // If there are details, format them nicely
        if (data.details && typeof data.details === "object") {
          const detailsArr = Object.entries(data.details).map(
            ([key, value]) => {
              // Convert camelCase to readable format
              const readableKey = key
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase());
              // Format numbers with commas
              const formattedValue =
                typeof value === "number" || !isNaN(parseFloat(value))
                  ? parseFloat(value).toLocaleString()
                  : value;
              return `${readableKey}: ${formattedValue}`;
            },
          );
          if (detailsArr.length > 0) {
            errorMsg += ` (${detailsArr.join(", ")})`;
          }
        }
        return errorMsg;
      }

      // Handle {message: "..."} format
      if (data.message) return data.message;

      // Handle {detail: "..."} format (Django REST framework)
      if (data.detail) return data.detail;

      // Handle array of errors
      if (Array.isArray(data) && data.length > 0) {
        return data
          .map((err) =>
            typeof err === "string" ? err : err.message || JSON.stringify(err),
          )
          .join(", ");
      }

      // Handle field validation errors {field: ["error1", "error2"]}
      if (typeof data === "object") {
        const fieldErrors = Object.entries(data)
          .filter(([key]) => key !== "error" && key !== "details")
          .map(([field, errors]) => {
            const fieldName = field
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase());
            const errorText = Array.isArray(errors)
              ? errors.join(", ")
              : errors;
            return `${fieldName}: ${errorText}`;
          });
        if (fieldErrors.length > 0) {
          return fieldErrors.join("; ");
        }
      }
    }

    // Try parsing msg as JSON if it looks like JSON
    if (
      response.msg &&
      typeof response.msg === "string" &&
      response.msg.startsWith("{")
    ) {
      try {
        const parsed = JSON.parse(response.msg);
        return Utilities.formatErrorMessage({ data: parsed }, fallbackMessage);
      } catch (e) {
        // Not valid JSON, return as is
      }
    }

    return response.msg || fallbackMessage;
  },

  /**
   * Show a formatted error message using antd message
   * @param {Object} response - The API response object from fetchWithAuth
   * @param {string} fallbackMessage - Default message if no error can be extracted
   */
  showError(response, fallbackMessage = "An error occurred") {
    const errorMsg = Utilities.formatErrorMessage(response, fallbackMessage);
    message.error(errorMsg);
  },
};

export default Utilities;
